import React from "react";
import { BiotechOutlined, VideocamOutlined } from "@mui/icons-material";
import { FaStethoscope } from "react-icons/fa";
import { DEVICE_WEBSOCKET_STATE } from "../../features/deviceWebSocket/deviceWebSocketClient";
import { CLINICAL_DEVICE_IDS } from "../../features/deviceWebSocket/deviceMessageProtocol";
import "./KioskClinicalDeviceStatus.css";

export const KIOSK_CLINICAL_DEVICE_STATUS_STATE = Object.freeze({
  CHECKING: "checking",
  CURRENT: "current",
  UNAVAILABLE: "unavailable",
});

const deviceDefinitions = [
  {
    id: CLINICAL_DEVICE_IDS.STETHOSCOPE,
    label: "Stethoscope",
    Icon: FaStethoscope,
  },
  {
    id: CLINICAL_DEVICE_IDS.EAR_CAMERA,
    label: "Ear camera",
    Icon: BiotechOutlined,
  },
  {
    id: CLINICAL_DEVICE_IDS.EXAM_CAMERA,
    label: "Exam camera",
    Icon: VideocamOutlined,
  },
];

const getSummary = (connectionState, statusState) => {
  if (connectionState !== DEVICE_WEBSOCKET_STATE.OPEN) return "Status link reconnecting";
  if (statusState === KIOSK_CLINICAL_DEVICE_STATUS_STATE.CURRENT) return "Live kiosk status";
  if (statusState === KIOSK_CLINICAL_DEVICE_STATUS_STATE.UNAVAILABLE) return "Status unavailable";
  return "Checking kiosk devices";
};

function KioskClinicalDeviceStatus({
  connectionState,
  snapshot,
  statusState = KIOSK_CLINICAL_DEVICE_STATUS_STATE.CHECKING,
  className = "",
  onDeviceAction,
  activeDeviceId = null,
  streamState = null,
  disabled = false,
}) {
  const isCurrent = connectionState === DEVICE_WEBSOCKET_STATE.OPEN
    && statusState === KIOSK_CLINICAL_DEVICE_STATUS_STATE.CURRENT;
  const currentDevices = isCurrent && Array.isArray(snapshot?.devices)
    ? snapshot.devices
    : [];
  const availabilityById = new Map(
    currentDevices.map(({ id, available }) => [id, available]),
  );
  const summary = getSummary(connectionState, statusState);

  return (
    <section
      className={`kiosk-clinical-device-status ${className}`.trim()}
      aria-label={`Kiosk clinical device status: ${summary}`}
      aria-live="polite"
    >
      <div className="kiosk-clinical-device-status__items">
        {deviceDefinitions.map(({ id, label, Icon }) => {
          const available = availabilityById.get(id);
          const tone = typeof available === "boolean"
            ? available ? "available" : "unavailable"
            : "unknown";
          const statusLabel = tone === "available"
            ? "Connected"
            : tone === "unavailable" ? "Not detected" : summary;
          const hasAction = typeof onDeviceAction === "function";
          const isActive = activeDeviceId === id;
          const canAct = hasAction && !disabled && isCurrent && (available || isActive);
          const actionStatusLabel = isActive
            ? streamState === "streaming" ? "Streaming" : "Stream active"
            : statusLabel;

          return (
            <button
              type="button"
              key={id}
              className={`kiosk-clinical-device-status__item kiosk-clinical-device-status__item--${tone}${isActive ? " kiosk-clinical-device-status__item--active" : ""}`}
              data-device-id={id}
              onClick={canAct ? () => onDeviceAction(id) : undefined}
              disabled={!canAct}
              aria-pressed={isActive}
              aria-label={`${label}: ${actionStatusLabel}`}
              title={`${label}: ${actionStatusLabel}`}
            >
              <span className="kiosk-clinical-device-status__icon" aria-hidden="true">
                <Icon />
                <span className="kiosk-clinical-device-status__light" />
              </span>
              <span>{isActive ? `${label} · ${streamState || "active"}` : label}</span>
            </button>
          );
        })}
      </div>
      {!isCurrent && (
        <span className="kiosk-clinical-device-status__summary">{summary}</span>
      )}
    </section>
  );
}

export default KioskClinicalDeviceStatus;
